import { defineStore } from 'pinia';
import dayjs from 'dayjs';

export const useTravelPlusStore = defineStore('travelPlus', {
    state: () => ({
        consultsAvailable: 0,
        subscription: null,
        subscriptionPayments: [],
        loadingConsultations: false,
        loadingSubscription: false,
        loadingSubscriptionPayments: false,
        podcasts: [],
        loadingPodcasts: false,
        podcastsError: null,
        consultations: [],
        loadingEvents: false,
        consultationError: null,
    }),
    getters: {
        subscriptionEnds(state) {
            if (state.subscription?.ends_at) {
                return dayjs(state.subscription.ends_at).format('MMM DD, YYYY');
            }

            if (state.subscription?.current_period_end) {
                return dayjs
                    .unix(state.subscription.current_period_end)
                    .format('MMM DD, YYYY');
            }

            return null;
        },
        latestPodcasts: (state) => state.podcasts.slice(0, 3),
    },
    actions: {
        getDashboardData() {
            //
        },
        getConsultations() {
            this.loadingConsultations = true;

            ApiService.apiCall('/travel-plus/consultations', 'get')
                .then((response) => {
                    this.consultsAvailable = response.data.consults_available;
                    this.loadingConsultations = false;
                })
                .catch((response) => {
                    this.loadingConsultations = false;
                });
        },
        getSubscription() {
            this.loadingSubscription = true;

            ApiService.apiCall(`/travel-plus/subscription`, 'get')
                .then((response) => {
                    this.subscription = response.data;
                    this.loadingSubscription = false;
                })
                .catch((response) => {
                    this.loadingSubscription = false;
                });
        },
        async getSubscriptionPayments() {
            this.loadingSubscriptionPayments = true;

            try {
                const response = await ApiService.apiCall(
                    `/travel-plus/subscription/payments`,
                    'get'
                );
                this.subscriptionPayments = response.data;
            } catch (e) {
                // No invoices available
            } finally {
                this.loadingSubscriptionPayments = false;
            }
        },
        async cancelSubscription(
            cancelReason = null,
            cancelDescription = null
        ) {
            this.loadingSubscription = true;
            try {
                const response = await ApiService.apiCall(
                    `/travel-plus/subscription`,
                    'put',
                    {
                        status: 'cancel',
                        cancel_reason: cancelReason,
                        cancel_description: cancelDescription,
                    }
                );
                this.subscription = response.data;
            } finally {
                this.loadingSubscription = false;
            }
        },
        async cancelSubscriptionWithRefund(
            cancelReason = null,
            cancelDescription = null
        ) {
            this.loadingSubscription = true;
            try {
                const response = await ApiService.apiCall(
                    `/travel-plus/subscription`,
                    'put',
                    {
                        status: 'cancel_with_refund',
                        cancel_reason: cancelReason,
                        cancel_description: cancelDescription,
                    }
                );
                this.subscription = response.data;
            } finally {
                this.loadingSubscription = false;
            }
        },
        async renewSubscription(paymentMethodId = null) {
            this.loadingSubscription = true;
            try {
                const payload = { status: 'renew' };
                if (paymentMethodId) {
                    payload.payment_method = paymentMethodId;
                }
                const response = await ApiService.apiCall(
                    `/travel-plus/subscription`,
                    'put',
                    payload
                );
                this.subscription = response.data;
            } finally {
                this.loadingSubscription = false;
            }
        },
        async previewUpgradeToAnnual() {
            const response = await ApiService.apiCall(
                '/billing/upgrade-preview',
                'get'
            );
            return response.data;
        },
        async upgradeToAnnual(promoCode = null) {
            this.loadingSubscription = true;
            try {
                const response = await ApiService.apiCall(
                    '/billing/upgrade-to-annual',
                    'post',
                    { promo_code: promoCode }
                );
                return response.data;
            } finally {
                this.loadingSubscription = false;
            }
        },
        async switchToMonthly() {
            this.loadingSubscription = true;
            try {
                const response = await ApiService.apiCall(
                    '/billing/switch-to-monthly',
                    'post'
                );
                return response.data;
            } finally {
                this.loadingSubscription = false;
            }
        },
        async fetchPodcasts() {
            this.loadingPodcasts = true;
            this.podcastsError = null;

            try {
                const response = await ApiService.apiCall('/podcasts', 'get');
                this.podcasts = response.data.map((episode) => ({
                    id: episode.id,
                    image: episode.image,
                    title: episode.title,
                    date: episode.date,
                    duration: episode.duration || '00:00:00',
                    link: episode.link,
                }));
            } catch (e) {
                this.podcastsError =
                    'Failed to load episodes. Please try again later.';
                console.error('Error fetching episodes:', e);
            } finally {
                this.loadingPodcasts = false;
            }
        },

        async fetchConsultations() {
            this.loadingEvents = true;
            this.consultationError = null;

            try {
                const response = await ApiService.apiCall(
                    '/calendly/events',
                    'get'
                );
                this.consultations = response.data?.data || [];
            } catch (e) {
                this.consultationError =
                    'Failed to load consultations. Please try again later.';
                console.error('Error fetching consultations:', e);
            } finally {
                this.loadingEvents = false;
            }
        },

        async storeConsultation(eventUuid, inviteeUuid) {
            this.loadingEvents = true;
            this.consultationError = null;

            try {
                await ApiService.apiCall('/calendly/events', 'post', {
                    event_uuid: eventUuid,
                    invitee_uuid: inviteeUuid,
                });

                await this.fetchConsultations();
            } catch (e) {
                this.consultationError =
                    'Failed to store consultation. Please try again later.';
                console.error('Error storing consultation:', e);
            } finally {
                this.loadingEvents = false;
            }
        },
        removeConsultCount() {
            this.consultsAvailable = this.consultsAvailable - 1;
        },
        addConsultCount() {
            this.consultsAvailable = this.consultsAvailable + 1;
        },
    },
});
